import * as Actions from '@/app/store/actions';
import { Avatar, CircularProgress } from '@material-ui/core';
import Hls from 'hls.js';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import KinesisHistoryControls from './KinesisHistoryControls';
import config from '@/app/main/subadmin/livestreaming/live/hlsconfig';
import { useHistoryVideo } from '@/app/main/subadmin/livestreaming/history/HistoryProvider';
import GradientButton from '@/app/customComponents/GradientButton/GradientButton';
import { downloadClip, getDownloadClipProgress } from '@/app/services/liveStreaming/liveStreaming';
import DownloadProgressModal from '../DownloadProgressModal';
import MjpegVideoPlayer from '../MJPEG/MjpegVideoPlayer';

const KinesisHistoryPlayer = ({
	room,
	streamData,
	startTime,
	endTime,
	isFullscreen,
	setIsFullscreen,
	playbackTime,
	setPlaybackTime,
	isPlaying,
	setIsPlaying,
	selectedDate,
}) => {
	const dispatch = useDispatch();
	const { clipLoading } = useHistoryVideo();
	const [isLoading, setIsLoading] = useState(false);
	const hoverTimeout = useRef(null);
	const playerRef = useRef(null);
	const boxRef = useRef(null);
	const hls = useRef(null);
	const [downloadProgress, setDownloadProgress] = useState(0);
	const [downloadJobId, setDownloadJobId] = useState(null);
	const [downloadModalOpen, setDownloadModalOpen] = useState(false);
	const [downloadLink, setDownloadLink] = useState(null);
	const handleTimeUpdate = () => {
		const video = playerRef.current;
		if (video) {
			setPlaybackTime(video.currentTime);
		}
	};

	const handleBuffering = () => {
		setIsLoading(true);
	};

	const attachEventListeners = () => {
		const video = playerRef.current;
		const controlsContainer = boxRef.current;
		if (video && controlsContainer) {
			video.addEventListener('waiting', handleBuffering);
			video.addEventListener('timeupdate', handleTimeUpdate);
			video.addEventListener('playing', () => {
				setIsPlaying(true);
				setIsLoading(false);
				clearTimeout(hoverTimeout.current);
			});
			video.addEventListener('pause', () => {
				setIsPlaying(false);
			});
		}
	};

	const handleDownloadClip = async () => {
		if (!room || !startTime || !endTime || !selectedDate) {
			dispatch(
				Actions.showMessage({
					message: 'Invalid parameters for downloading clip.',
					autoHideDuration: 1500,
					variant: 'error',
				}),
			);
			return;
		}

		try {
			setDownloadProgress(0);
			const response = await downloadClip(room.id, `${selectedDate} ${startTime}`, `${selectedDate} ${endTime}`);
			const job_id = response.data.job_id;
			setDownloadJobId(job_id);

			// Start polling for progress
			pollDownloadProgress(job_id);
		} catch (error) {
			console.error('Error downloading clip:', error);
			dispatch(
				Actions.showMessage({
					message: 'Error initiating download.',
					autoHideDuration: 1500,
					variant: 'error',
				}),
			);
			setDownloadProgress(null);
		}
	};

	const pollDownloadProgress = async (jobId) => {
		try {
			setDownloadModalOpen(true);
			const progressResponse = await getDownloadClipProgress(jobId);
			const { status, progress, download_url } = progressResponse.data;

			setDownloadProgress(progress);

			// create a temp link, click it, and clean up
			if (status === 'completed' && progress === 100) {
				const link = document.createElement('a');
				link.href = download_url;
				link.setAttribute('download', '');
				link.style.display = 'none';
				document.body.appendChild(link);

				link.click();

				document.body.removeChild(link);

				setDownloadProgress(null);
				setDownloadJobId(null);
				setDownloadLink(download_url);
			} else if (status === 'failed') {
				dispatch(
					Actions.showMessage({
						message: 'Download failed.',
						autoHideDuration: 1500,
						variant: 'error',
					}),
				);
				setDownloadProgress(null);
				setDownloadJobId(null);
			} else {
				// Continue polling
				setTimeout(() => pollDownloadProgress(jobId), 3000);
			}
		} catch (error) {
			console.error('Error checking download progress:', error);
			setDownloadProgress(null);
			setDownloadJobId(null);
			setDownloadModalOpen(false);
		}
	};

	const initializeHls = (url) => {
		const video = playerRef.current;
		if (hls.current) {
			hls.current.destroy();
			hls.current = null;
		}
		hls.current = new Hls(config);
		hls.current.loadSource(url);
		hls.current.attachMedia(video);
		hls.current.on(Hls.Events.MEDIA_ATTACHED, () => {
			hls.current.on(Hls.Events.ERROR, () => {
				dispatch(
					Actions.showMessage({
						message: 'Error loading video stream.',
						autoHideDuration: 1500,
						variant: 'error',
					}),
				);
			});
			hls.current.on(Hls.Events.MANIFEST_PARSED, () => {
				video.currentTime = playbackTime;
				if (isPlaying) {
					video.play().catch((error) => {
						console.warn('Failed to play video:', error);
					});
				}
			});
		});
		attachEventListeners();
	};

	useEffect(() => {
		const video = playerRef.current;
		if (streamData?.session_url) {
			if (video) {
				if (Hls.isSupported()) {
					initializeHls(streamData.session_url);
				} else {
					video.src = streamData.session_url;
				}
			}
		}

		return () => {
			if (streamData && streamData?.video_type === 'hls') {
				clearTimeout(hoverTimeout.current);
				if (hls.current) {
					hls.current.destroy();
					hls.current = null;
				}
				if (video) {
					video.removeEventListener('waiting', handleBuffering);
					video.removeEventListener('timeupdate', handleTimeUpdate);
					video.removeEventListener('pause', () => {});
				}
			}
		};
	}, [streamData]);

	useEffect(() => {
		const video = playerRef.current;
		if (isFullscreen && video) {
			video.currentTime = playbackTime;
			if (isPlaying) {
				video.play().catch((error) => {
					console.warn('Failed to play video:', error);
				});
			}
		}
	}, [isFullscreen]);

	useEffect(() => {
		const video = playerRef.current;
		if (video) {
			video.currentTime = 0;
		}
	}, [startTime]);

	return (
		<div>
			<DownloadProgressModal
				downloadLink={downloadLink}
				open={downloadModalOpen}
				progress={downloadProgress}
				setDownloadLink={setDownloadLink}
				setOpen={setDownloadModalOpen}
			/>
			<div className="flex justify-start items-start bg-black rounded-lg relative overflow-hidden" ref={boxRef}>
				<div className={`w-full h-full relative ${!isFullscreen ? 'aspect-ratio-16-9' : ''}`}>
					{streamData?.session_url && streamData?.video_type === 'hls' ? (
						<div
							className={` ${isFullscreen ? 'h-full w-full fixed top-0 left-0 z-9999' : 'relative overflow-hidden'}`}
						>
							<video
								autoPlay
								className=" history-video-player object-fill"
								id="myVideo"
								muted
								playsInline
								preload="none"
								ref={playerRef}
							/>
							{streamData?.overlay_image ? (
								<img
									alt=""
									className="absolute top-0 left-0 z-99 w-full h-full object-fill"
									src={streamData?.overlay_image}
								/>
							) : null}
							{isLoading ? (
								<div className="loading-spinner-container">
									<CircularProgress size={35} />
								</div>
							) : null}
							<div className="absolute bottom-0 w-full">
								<KinesisHistoryControls
									boxRef={boxRef}
									handleIphoneFullScreenToggle={() => {
										setIsFullscreen(!isFullscreen);
									}}
									playerRef={playerRef}
									room={room?.id}
									setIsFullscreen={setIsFullscreen}
									startTime={startTime}
									// showControls={showControls}
								/>
							</div>
							<div className="absolute text-white text-pd-lg font-bold top-0 left-0 px-12 flex items-center py-32 h-28 gap-10">
								<Avatar src={room?.thumb} />
								<div>{room?.name}</div>
							</div>
						</div>
					) : null}
				</div>
				{!startTime ? (
					<div className="stream-not-running-text-history">Please select a clip</div>
				) : clipLoading ? (
					<div className="stream-not-running-text-history bg-black w-full h-full flex justify-center items-center">
						<CircularProgress size={35} />
					</div>
				) : startTime && !streamData?.session_url && !isLoading ? (
					<div className="stream-not-running-text-history">No video</div>
				) : null}
			</div>
			{streamData?.session_url ? (
				<div className="flex justify-end m-16">
					<GradientButton disabled={downloadJobId !== null} onClick={handleDownloadClip} variant="royal-blue">
						{downloadJobId ? 'Downloading...' : 'Download Clip'}
					</GradientButton>
				</div>
			) : null}
		</div>
	);
};

export default KinesisHistoryPlayer;
